<template>
  <v-container fluid grid-list-xl>
    <v-layout row wrap>
      <v-flex xs12>
        <v-card>
          <v-toolbar color="primary" dark>
            <v-toolbar-title>{{ $t('externalApis') }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn color="success" @click="openGenerateDialog">
              <v-icon left>add</v-icon>
              {{ $t('generateNewApiKey') }}
            </v-btn>
          </v-toolbar>
          
          <div class="my-2">
            <div v-if="loading" class="text-xs-center pa-5">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              ></v-progress-circular>
              <p class="mt-3">{{ $t('loadingApiKeys') }}</p>
            </div>
            
            <div v-else-if="error" class="text-xs-center pa-5">
              <v-icon color="error" size="64">error</v-icon>
              <p class="mt-3 error--text">{{ error }}</p>
              <v-btn color="primary" @click="fetchApiKeys">
                {{ $t('retry') }}
              </v-btn>
            </div>
            
            <div v-else>
              <v-alert v-if="!apiKeys.length" type="info" value="true" class="mb-4">
                {{ $t('noApiKeysFound') }}
              </v-alert>
              
              <v-data-table
                v-else
                :headers="headers"
                :items="apiKeys"
                :hide-actions="true"
                class="elevation-1"
              >
                <template v-slot:items="props">
                  <td>{{ props.item.name }}</td>
                  <td>
                    <div class="d-flex align-center">
                      <code class="api-key-display">{{ getDisplayKey(props.item) }}</code>
                      <v-btn
                        icon
                        small
                        @click="toggleKeyVisibility(props.item.id)"
                        class="ml-2"
                      >
                        <v-icon small>
                          {{ isKeyVisible(props.item.id) ? 'visibility_off' : 'visibility' }}
                        </v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        small
                        @click="copyToClipboard(props.item.key)"
                        class="ml-1"
                        v-if="isKeyVisible(props.item.id)"
                      >
                        <v-icon small>content_copy</v-icon>
                      </v-btn>
                    </div>
                  </td>
                  <td>{{ formatDate(props.item.created_at) }}</td>
                  <td>
                    <v-btn
                      icon
                      color="error"
                      @click="confirmDelete(props.item)"
                      small
                    >
                      <v-icon small>delete</v-icon>
                    </v-btn>
                  </td>
                </template>
              </v-data-table>
            </div>
          </div>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- TODO Sahil: Make sharable common components for these dialogs -->
    <!-- Generate New API Key Dialog -->
    <v-dialog v-model="generateDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('generateNewApiKey') }}</span>
        </v-card-title>
        
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-alert type="warning" value="true" class="mb-4">
                  {{ $t('apiKeyGenerationWarning') }}
                </v-alert>
                
                <v-text-field
                  v-model="newApiKeyName"
                  :label="$t('apiKeyName')"
                  :placeholder="$t('apiKeyNamePlaceholder')"
                  :rules="nameRules"
                  required
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeGenerateDialog">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn 
            color="success" 
            @click="generateApiKey"
            :loading="generating"
            :disabled="!newApiKeyName || newApiKeyName.length < 3"
          >
            {{ $t('generate') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline error--text">
          {{ $t('confirmDeleteApiKey') }}
        </v-card-title>
        
        <v-card-text>
          {{ $t('deleteApiKeyConfirmMessage', { name: selectedApiKey && selectedApiKey.name }) }}
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="deleteDialog = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn 
            color="error" 
            @click="deleteApiKey"
            :loading="deleting"
          >
            {{ $t('delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Dialog for New API Key -->
    <v-dialog v-model="successDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline success--text">
          <v-icon color="success" class="mr-2">check_circle</v-icon>
          {{ $t('apiKeyGenerated') }}
        </v-card-title>
        
        <v-card-text>
          <v-alert type="success" value="true" class="mb-4">
            {{ $t('apiKeyGeneratedMessage') }}
          </v-alert>
          
          <v-text-field
            v-model="newGeneratedKey"
            :label="$t('yourNewApiKey')"
            readonly
            outlined
            class="code-field"
          >
            <template v-slot:append>
              <v-btn
                icon
                @click="copyToClipboard(newGeneratedKey)"
              >
                <v-icon>content_copy</v-icon>
              </v-btn>
            </template>
          </v-text-field>
          
          <v-alert type="warning" value="true">
            {{ $t('apiKeySecurityWarning') }}
          </v-alert>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="closeSuccessDialog">
            {{ $t('understood') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import ApiKey from '@/client/api-key'

export default {
  name: 'ExternalApis',
  
  data () {
    return {
      apiKeys: [],
      loading: true,
      error: null,
      generateDialog: false,
      deleteDialog: false,
      successDialog: false,
      newApiKeyName: '',
      selectedApiKey: null,
      generating: false,
      deleting: false,
      newGeneratedKey: '',
      visibleKeys: new Set(),
      nameRules: [
        v => !!v || this.$t('nameRequired'),
        v => (v && v.length >= 3) || this.$t('nameMinLength'),
        v => (v && v.length <= 50) || this.$t('nameMaxLength')
      ]
    }
  },
  
  computed: {
    headers () {
      return [
        { text: this.$t('name'), value: 'name', sortable: false },
        { text: this.$t('apiKey'), value: 'key', sortable: false },
        { text: this.$t('createdAt'), value: 'created_at', sortable: false },
        { text: this.$t('actions'), value: 'actions', sortable: false, width: '100px' }
      ]
    }
  },
  
  async mounted () {
    await this.fetchApiKeys()
  },
  
  methods: {
    async fetchApiKeys () {
      this.loading = true
      this.error = null
      
      try {
        const response = await ApiKey.all()
        this.apiKeys = response || []
      } catch (error) {
        console.error('Error fetching API keys:', error)
        this.error = this.$t('errorLoadingApiKeys')
      } finally {
        this.loading = false
      }
    },
    
    openGenerateDialog () {
      this.newApiKeyName = ''
      this.generateDialog = true
    },
    
    closeGenerateDialog () {
      this.generateDialog = false
      this.newApiKeyName = ''
    },
    
    async generateApiKey () {
      if (!this.newApiKeyName || this.newApiKeyName.length < 3) return
      
      this.generating = true
      
      try {
        const response = await ApiKey.replaceApiKey({ name: this.newApiKeyName })
        this.newGeneratedKey = response.data.key
        this.generateDialog = false
        this.successDialog = true
        await this.fetchApiKeys()
      } catch (error) {
        console.error('Error generating API key:', error)
        this.$store.dispatch('setNotification', {
          message: this.$t('errorGeneratingApiKey'),
          type: 'error'
        })
      } finally {
        this.generating = false
      }
    },
    
    confirmDelete (apiKey) {
      this.selectedApiKey = apiKey
      this.deleteDialog = true
    },
    
    async deleteApiKey () {
      if (!this.selectedApiKey) return
      
      this.deleting = true
      
      try {
        await ApiKey.destroyById(this.selectedApiKey.id)
        this.deleteDialog = false
        this.selectedApiKey = null
        await this.fetchApiKeys()
        this.$store.dispatch('setNotification', {
          message: this.$t('apiKeyDeleted'),
          type: 'success'
        })
      } catch (error) {
        console.error('Error deleting API key:', error)
        this.$store.dispatch('setNotification', {
          message: this.$t('errorDeletingApiKey'),
          type: 'error'
        })
      } finally {
        this.deleting = false
      }
    },
    
    closeSuccessDialog () {
      this.successDialog = false
      this.newGeneratedKey = ''
    },
    
    toggleKeyVisibility (keyId) {
      if (this.visibleKeys.has(keyId)) {
        this.visibleKeys.delete(keyId)
      } else {
        this.visibleKeys.add(keyId)
      }
      // Force reactivity
      this.visibleKeys = new Set(this.visibleKeys)
    },
    
    isKeyVisible (keyId) {
      return this.visibleKeys.has(keyId)
    },
    
    getDisplayKey (apiKey) {
      if (this.isKeyVisible(apiKey.id)) {
        return apiKey.key
      }
      return '*'.repeat(32)
    },
    
    async copyToClipboard (text) {
      try {
        await navigator.clipboard.writeText(text)
        this.$store.dispatch('setNotification', {
          message: this.$t('copiedToClipboard'),
          type: 'success'
        })
      } catch (error) {
        console.error('Error copying to clipboard:', error)
        this.$store.dispatch('setNotification', {
          message: this.$t('errorCopyingToClipboard'),
          type: 'error'
        })
      }
    },
    
    formatDate (dateString) {
      return new Date(dateString).toLocaleDateString(this.$i18n.locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.api-key-display {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 4px;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.code-field {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.code-field >>> input {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
}
</style>